// src/lib/apps.ts — app registry.
//
// The single source of truth for what tiles appear on the Apps page and the
// Home quick-links grid. Each entry uses a string `iconKey` rather than a
// React component so this module can be plain TS — the AppTile primitive
// resolves the key to an Icon at render time.
//
// Permission flags follow the simplified gating decision in STATUS §3
// (2026-05-14) and the canonical statement in
// docs/build-plan/screens/_OVERRIDE_admin_gating.md:
//   - me.isAdmin is the sole admin gate for new admin-write actions.
//   - me.isNewsAdmin and me.isAlertsAdmin remain as per-resource sub-flags
//     so a non-admin user can edit a single resource if granted.
//
// Phase 9 -- each app also carries an optional `appKey` matching the keys
// in me.apps (AppPermissions). When set, the tile only renders for users
// whose me.apps[appKey] is true. Backend enforces independently; this is
// chrome-level hiding only. App-links (external tiles) and the role-gated
// admin variants don't use appKey -- they use existing role gates.
//
// Phase 9 follow-up (2026-05-21) -- D2: `ssoOnly` flag for external NYMR
// apps that are only reachable behind staff SSO. PIN volunteers don't have
// SSO sessions for those apps and the tile would dead-end at a login page
// they can't satisfy, so we hide it from them entirely. Reports / Events /
// Risks / Objectives all gate this way; Live Trains stays available to
// everyone (it's a public departures board).

import type { AppPermissions, FeatureFlags, MeResponse } from "../auth";

export type AppTone = "brass" | "heather" | "sage" | "signal" | "paper";

export type AppGroup = "daily" | "for-you" | "manage" | "app-links";

export type AppSize = "lg" | "sm";

// isSsoAdmin (Sign-In System Build 1) gates the System Preferences tile to real
// SSO admins — PIN admins can't reach /api/access/* and would dead-end at 403.
export type AppPerm = "isAdmin" | "isNewsAdmin" | "isAlertsAdmin" | "isSsoAdmin";

export interface AppEntry {
  id: string;
  name: string;
  desc: string;
  iconKey: string; // resolved by AppTile via Icons.<key>
  tone: AppTone;
  group: AppGroup;
  /** Internal SPA route (starts "/") or external URL (starts "https://"). */
  route: string;
  perm?: AppPerm;
  /** Phase 9 -- per-app permission key from me.apps. When set, tile hides
   *  unless me.apps[appKey] is true. */
  appKey?: keyof AppPermissions;
  /** Sign-In System (Build 1) -- system-pref feature kill-switch from
   *  me.features. When set, the tile hides if the flag is false (admin turned
   *  the feature off). Independent of the per-user appKey gate. */
  featureKey?: keyof FeatureFlags;
  /** Sign-In System (Build 1) -- custom visibility predicate. When present it
   *  REPLACES the single-value `perm` check (still subject to the ssoOnly /
   *  appKey / featureKey gates above it). Used for tiles whose audience is an
   *  OR of several conditions, e.g. Sign In Timesheet = admin OR HR OR a
   *  sign-in-enabled user. */
  visibleWhen?: (ctx: PermissionContext) => boolean;
  /** Phase 9 follow-up (D2) -- when true, the tile is hidden for PIN users.
   *  Used for external NYMR apps that require Entra SSO and would otherwise
   *  dead-end PIN volunteers at a login page they can't satisfy. */
  ssoOnly?: boolean;
  /** Tile size — "sm" used by the app-links group; default "lg". */
  size?: AppSize;
}

export const APPS: ReadonlyArray<AppEntry> = [
  // ── daily ───────────────────────────────────────────────────────────────────
  { id: "contacts",    name: "Contacts",        desc: "Directory and emergency",     iconKey: "Users",     tone: "brass",   group: "daily",  route: "/contacts", appKey: "contacts" },
  { id: "duty",        name: "Duty",            desc: "Today's roster and rota",     iconKey: "Cal",       tone: "heather", group: "daily",  route: "/duty",     appKey: "duty" },
  { id: "reports",     name: "Reports",         desc: "Daily trading at a glance",   iconKey: "Bars",      tone: "brass",   group: "daily",  route: "/reports",  appKey: "reports" },
  // Phase 9 / Cut 1 -- Calendar (events at NYMR). Sage per kickoff gotcha #16
  // (other Daily tiles already use brass/heather/signal). Icon "Cal" shared
  // with Duty -- acceptable; both are calendar-shaped surfaces.
  { id: "calendar",    name: "Calendar",        desc: "Events at NYMR",              iconKey: "Cal",       tone: "sage",    group: "daily",  route: "/calendar" },
  { id: "alerts",      name: "Alerts",          desc: "Notices from management",     iconKey: "Alert",     tone: "signal",  group: "daily",  route: "/alerts",   appKey: "alerts" },
  { id: "docs",        name: "Documents",       desc: "Policies, notices, guides",   iconKey: "Doc",       tone: "brass",   group: "daily",  route: "/docs",     appKey: "docs" },

  // ── for you ───────────────────────────────────────────────────────────────
  { id: "weekly",      name: "Weekly Sheet",    desc: "What's running this week",    iconKey: "Train",     tone: "sage",    group: "for-you", route: "/weekly",    appKey: "weekly" },
  { id: "tickets",     name: "Comp Tickets",    desc: "Your allocation & visits",    iconKey: "Ticket",    tone: "heather", group: "for-you", route: "/tickets",   appKey: "tickets" },
  // ID Card — digital twin of the NYMR PVC ID card. A DEFAULT app for every
  // user: no perm / appKey / ssoOnly gate, so it always renders for SSO and
  // PIN sessions alike (ID_CARD_SPEC §7). The card reads the session user.
  { id: "idcard",      name: "ID Card",         desc: "Your NYMR ID card",           iconKey: "User",      tone: "heather", group: "for-you", route: "/idcard" },
  // Confirmations & Consents (onboarding, migration 080) — documents the user
  // must read & accept. A default app for everyone (no gate); the orange Home
  // banner is the urgent nudge, this tile the calm always-available entry point.
  { id: "consents",    name: "Confirmations & Consents", desc: "Read & accept required documents", iconKey: "Check", tone: "sage", group: "for-you", route: "/consents" },
  // Driving Licence Check — driver self-service (mobile). Gated on the
  // apps.drivingLicenceCheck per-user grant set in the Access Apps grid. The
  // page self-selects the driver view; DV Admins get the admin tile below.
  { id: "licence-check", name: "Driving Licence", desc: "Check & submit your licence", iconKey: "Licence", tone: "sage", group: "for-you", route: "/licence-check?v=driver", appKey: "drivingLicenceCheck" },
  // Long Service Travel Pass — recipient self-service. Gated on the
  // apps.longService per-user grant (Access Apps grid). The LS Admin console
  // is a separate manage-group tile gated by is_long_service_admin.
  { id: "long-service", name: "Long Service Pass", desc: "Your travel pass & awards", iconKey: "Ticket", tone: "heather", group: "for-you", route: "/long-service", appKey: "longService" },
  // Training & Competencies — My Training (recipient self-service). Gated on the
  // apps.training per-user flag, which the server derives true once the user has a
  // matched iHasco training record, so the tile appears only when there's data.
  { id: "training", name: "My Training", desc: "Your training & certificates", iconKey: "Check", tone: "sage", group: "for-you", route: "/training", appKey: "training" },
  { id: "vacancies",   name: "Vacancies",       desc: "Open roles & volunteer",      iconKey: "Briefcase", tone: "sage",    group: "for-you", route: "/vacancies", appKey: "vacancies" },
  // Rostering (2026-07-27) — a thin PERSONAL view onto rostering.nymr.co.uk for
  // the signed-in volunteer (my turns, availability, clock-on & compliance).
  // Three-layer gating: appKey "rostering" hides the tile unless me.apps.rostering
  // (off by default server-side — see api DEFAULT_APPS), the /rostering route
  // self-gates, and the /api/rostering/* proxy re-checks session.apps.rostering.
  // Migration 147 seeds the grant to chris.flynn@ only, so it stays invisible
  // until released.
  // NOT ssoOnly (changed 2026-07-28). rostering.nymr.co.uk is a MANAGER/CLERK
  // tool — only managers, roster clerks and admins sign in there. Every
  // volunteer and member of staff is meant to use rostering through THIS
  // applet, and most of them are PIN users, so ssoOnly was hiding the tile
  // from the very people it was built for. The per-user apps.rostering grant
  // stays the gate (and the server proxy re-checks it), and nothing in the
  // applet dead-ends a PIN session any more — exams are now sat in-app rather
  // than by bouncing to the rostering website's Entra login.
  { id: "rostering",   name: "Rostering",       desc: "My turns, availability, clock-on & compliance", iconKey: "Cal", tone: "heather", group: "for-you", route: "/rostering", appKey: "rostering" },
  // Old Newsletter reader (the /nlcreator-composed themed issues) — DISABLED for
  // now (2026-07): the members' newsletter moves to the uploaded-PDF "Newsletter
  // Reader" app below. The route + backend stay so the CEO Newsletter reader
  // (/newsletter/ceo/:id) keeps working; only this reader tile is withdrawn.
  // { id: "newsletter",  name: "Newsletter",      desc: "Latest stories from NYMR",    iconKey: "News",      tone: "heather", group: "for-you", route: "/newsletter",appKey: "newsletter" },
  // Newsletter Reader (migration 124) — the members' newsletter as an uploaded-PDF
  // magazine (a Moorsline sibling). Gated by apps.newsletterReader, which is ON by
  // default for everyone (server DEFAULT_APPS) so it behaves like universal reading
  // while staying withdrawable per-user in User Access. The publisher console is a
  // separate manage-group tile gated by isNewsletterPublisher.
  { id: "newsletter-reader", name: "Newsletter Reader", desc: "Read the latest NYMR Newsletter", iconKey: "News", tone: "heather", group: "for-you", route: "/newsletter-reader", appKey: "newsletterReader" },
  // Moorsline — the members' magazine. A DEFAULT app for everyone (no perm /
  // appKey gate), so it always renders. Reading is universal; the editor console
  // is a separate manage-group tile gated by isMoorslineEditor (editor build).
  { id: "moorsline",   name: "Moorsline",       desc: "The members' magazine",       iconKey: "News",      tone: "brass",   group: "for-you", route: "/moorsline" },
  // Mini-Blog (migration 128) — department-scoped micro-articles. Reader tile;
  // on by default via DEFAULT_APPS (the department filter is the audience gate).
  { id: "miniblog",    name: "Mini-Blog",       desc: "Short updates from your department", iconKey: "News", tone: "sage", group: "for-you", route: "/miniblog", appKey: "miniBlog" },
  // Mini-Blog Creator — the Author surface. Granted per-user in User Access
  // (apps.miniBlogCreator); authors publish to their own department(s) only.
  { id: "miniblog-creator", name: "Mini-Blog Creator", desc: "Post updates for your department", iconKey: "Edit", tone: "brass", group: "for-you", route: "/miniblog/creator", appKey: "miniBlogCreator" },
  // Sign-In System (Build 1). Visible to everyone (your own shift history);
  // SSO admins additionally get the all-users view inside the page. Hidden when
  // an admin turns features.timesheet_app off.
  // Visible to admins, HR admins, and any sign-in-enabled user (own history);
  // hidden from everyone else. SSO admins + HR additionally get the all-users
  // view inside the page. Still subject to the features.timesheet_app switch.
  { id: "timesheet",   name: "Sign In Timesheet", desc: "Your shifts & sign-in records", iconKey: "Cal",     tone: "sage",    group: "for-you", route: "/timesheet", featureKey: "timesheetApp", visibleWhen: (c) => Boolean(c.isAdmin || c.isHrAdmin || c.signInEnabled) },
  // Issue Manager (CODE-001). Sage tone keeps it visually distinct from the
  // brass/heather Daily/For-you cluster. Lives in `daily` because reporting
  // an issue is an everyday action for any volunteer or staff member.
  // Issue Manager is the triage/admin console — visible only to admins and to
  // users granted manager rights inside the app (me.issues.manager), NOT to
  // ordinary volunteers. (Was previously an appKey gate that fell open to all.)
  { id: "issues",      name: "Issue Manager",   desc: "Track & manage issues",       iconKey: "Wrench",    tone: "sage",    group: "daily",  route: "/issues",   visibleWhen: (c) => Boolean(c.isAdmin || c.issuesManager) },
  // Raise an Issue (split out of Issue Manager). Its own app + User Access
  // permission (apps.raiseIssue), OFF for everyone by default. The reporter
  // (Raise + My Issues) lives here now; Issue Manager keeps triage/admin only.
  { id: "raise-issue", name: "Raise an Issue",  desc: "Report something that needs fixing", iconKey: "Alert", tone: "signal",  group: "daily",  route: "/raise-issue", appKey: "raiseIssue" },

  // ── manage ───────────────────────────────────────────────────────────────
  // Slice L / P2: the "Alerts admin" tile was removed -- the user-facing
  // /alerts page already covers both read and edit (admin / alerts-admin
  // gated server-side via canEditAlerts), so a separate tile is dead weight.
  { id: "duty-admin",       name: "Duty scheduler",     desc: "Assign and remove duty",  iconKey: "Wrench",   tone: "brass",   group: "manage", route: "/duty",            perm: "isAdmin", appKey: "duty" },
  { id: "newsletter-admin", name: "Newsletter editor",  desc: "Compose and publish",     iconKey: "Edit",     tone: "brass",   group: "for-you", route: "/nlcreator",      perm: "isAdmin", appKey: "nlcreator" },
  // Article Writer (Slice B) -- app-gated (appKey only, no perm) so it shows
  // for whoever an admin grants it; off for everyone by default.
  { id: "article-writer",   name: "Article Writer",     desc: "Write newsletter articles", iconKey: "Edit",    tone: "sage",    group: "for-you", route: "/article-writer", appKey: "articleWriter" },
  // Vacancies Manager (V2, 2026-11-04) — desktop editor + applications inbox.
  // SSO admin OR the Vacancies Manager role (migration 169); the API applies
  // the same gate and PIN sessions are always refused, hence ssoOnly.
  { id: "vacancies-admin",  name: "Vacancies Manager",  desc: "Edit, publish & review applications", iconKey: "Briefcase", tone: "brass", group: "manage", route: "/vacancies/admin", ssoOnly: true, visibleWhen: (c) => Boolean(c.isSsoAdmin || c.isVacanciesManager) },
  { id: "access-admin",     name: "User access",        desc: "Approve and manage users", iconKey: "Settings", tone: "brass", group: "manage", route: "/access/users",     perm: "isAdmin", appKey: "userAccess" },
  // HR Audits — staff records + audit trail. Admin OR HR Admin (visibleWhen OR-gate).
  { id: "hr-audits",        name: "HR Audits",          desc: "Staff records & audit trail",   iconKey: "History",  tone: "brass",   group: "manage", route: "/hr-audits",       visibleWhen: (c) => Boolean(c.isAdmin || c.isHrAdmin) },
  // Live Tracker — GPS tracker fleet health + link to the public live map.
  // Same gate as HR Audits: Admin OR HR Admin (visibleWhen OR-gate); the API
  // (/api/live-tracker/*) enforces the same check server-side.
  { id: "live-tracker",     name: "Live Tracker",       desc: "GPS fleet health & live map",   iconKey: "Pin",      tone: "sage",    group: "manage", route: "/live-tracker",    visibleWhen: (c) => Boolean(c.isAdmin || c.apps?.liveTracker) },
  { id: "camview",          name: "CamView",            desc: "Live station CCTV snapshots",   iconKey: "Camera", tone: "heather", group: "for-you", route: "/camview",         visibleWhen: (c) => Boolean(c.isAdmin || c.apps?.camView) },
  // CamView Pro (2026-08-10) — extended CCTV set. Its own grant (apps.camViewPro),
  // off by default; admins always have access. Shown in the manage lane since it
  // exposes operational yard/crossing cameras.
  { id: "camview-pro",      name: "CamView Pro",        desc: "Extended station & yard CCTV",  iconKey: "Camera", tone: "heather", group: "manage", route: "/camview-pro",     visibleWhen: (c) => Boolean(c.isAdmin || c.apps?.camViewPro) },
  // Out Of Office (migration 150) — set the auto-reply on managed shared
  // mailboxes. Admin OR the apps.outOfOffice grant; /api/ooo/* re-checks.
  { id: "out-of-office",    name: "Out Of Office",      desc: "Shared mailbox auto-replies",   iconKey: "Mail",     tone: "brass",   group: "manage", route: "/out-of-office",   visibleWhen: (c) => Boolean(c.isAdmin || c.apps?.outOfOffice) },
  // People — Onboarding & Offboarding (build-plan 38). Desktop HR module; full
  // staff lifecycle. Admin OR HR Admin (HR sees personal data; standard admins
  // get a server-redacted view). Heather lane (HR), like Group Manager.
  { id: "people",           name: "People",             desc: "Onboarding & offboarding",      iconKey: "Users",    tone: "heather", group: "manage", route: "/people",          visibleWhen: (c) => Boolean(c.isAdmin || c.isHrAdmin) },
  // Training & Competencies — Admin console (iHasco register, Unmatched imports
  // queue, audit). Admin OR HR Admin (visibleWhen OR-gate); every endpoint re-checks.
  { id: "training-admin",   name: "Training (Admin)",   desc: "iHasco register & matching queue", iconKey: "Doc",  tone: "brass",   group: "manage", route: "/training/admin",  visibleWhen: (c) => Boolean(c.isAdmin || c.isHrAdmin || c.isTrainingAdmin) },
  // Sign-In System (Build 1) -- admin-tunable feature settings + role/location
  // catalogues. SSO admin only (writes go through /api/access/*).
  { id: "prefs",            name: "System Preferences", desc: "Feature settings & catalogues", iconKey: "Settings", tone: "brass", group: "manage", route: "/prefs",          perm: "isSsoAdmin" },
  // Sign-In System (Build 2) -- Group Manager: chat-group CRUD + membership.
  // SSO admin only (writes go through /api/access/groups/*). Heather lane.
  { id: "group-manager",    name: "Group Manager",      desc: "Chat groups & membership",      iconKey: "Users",    tone: "heather", group: "manage", route: "/group-manager", perm: "isSsoAdmin" },
  // Info Hub Scheduler -- universal control surface for the background jobs
  // (lone-working escalation, duty auto sign-off, Group Message housekeeping).
  // SSO admin only (writes go through /api/scheduler/*; the tick is secret-gated).
  { id: "scheduler",        name: "Scheduler",          desc: "Background jobs & timing",      iconKey: "Clock",    tone: "heather", group: "manage", route: "/scheduler",     perm: "isSsoAdmin" },
  // AP Auto-Forward (migration 104) — kill-switch + dry-run/live control for the
  // experimental AP Invoice Auto-Forward Logic App. SSO admin only (writes go
  // through /api/ap-autoforward/*, requireSsoAdmin).
  { id: "ap-autoforward",   name: "AP Auto-Forward",    desc: "Status & dry-run/live control", iconKey: "Mail",     tone: "heather", group: "manage", route: "/ap-autoforward", perm: "isSsoAdmin" },
  // Driving Licence Check — DV Admin desktop view (roster, manual DVLA
  // verification, suspend/reinstate, fleet VES compliance). Gated on the
  // is_dvcheck_admin role flag (me.isDvCheckAdmin), set in the Access Roles grid.
  { id: "licence-check-admin", name: "Driving Licence (DV Admin)", desc: "Verify drivers & fleet compliance", iconKey: "Licence", tone: "brass", group: "manage", route: "/licence-check?v=admin", visibleWhen: (c) => Boolean(c.isDvCheckAdmin) },
  // Long Service Awards — LS Admin console (enrolment, service periods, the
  // production/print queue, award settings). Gated on the is_long_service_admin
  // role flag (me.isLongServiceAdmin), set in the Access Roles grid. The
  // recipient self-service side is the "Long Service Pass" app toggle above.
  { id: "long-service-admin", name: "Long Service (Admin)", desc: "Awards, enrolment & production", iconKey: "Star", tone: "brass", group: "manage", route: "/long-service/admin", visibleWhen: (c) => Boolean(c.isLongServiceAdmin) },
  // Moorsline editor console — upload / publish / schedule / delete editions.
  // Gated on the is_moorsline_editor role flag (me.isMoorslineEditor), set in
  // User Access. Reading the magazine is universal (the "Moorsline" tile above).
  { id: "moorsline-editor", name: "Moorsline Editor", desc: "Upload & publish magazine editions", iconKey: "Edit", tone: "brass", group: "manage", route: "/moorsline/editor", visibleWhen: (c) => Boolean(c.isMoorslineEditor) },
  // NYMR Newsletter Publisher (migration 124) — upload / publish / schedule /
  // delete newsletter editions. Gated on the is_newsletter_publisher role flag
  // (me.isNewsletterPublisher), set in User Access. Reading is the on-by-default
  // apps.newsletterReader grant (the "Newsletter Reader" tile above).
  { id: "newsletter-publisher", name: "NYMR Newsletter Publisher", desc: "Upload & publish newsletter editions", iconKey: "Edit", tone: "brass", group: "manage", route: "/newsletter-reader/editor", visibleWhen: (c) => Boolean(c.isNewsletterPublisher) },
  // Incident Log (H&S, migration 101) — SSO-only control app. Shows for Read
  // Only (apps.incidentLog) or Full Access (isIncidentAdmin). Full Access users
  // may hold only the flag (no app-grant), so this is a visibleWhen OR-gate
  // rather than an appKey gate (which would hide them when apps.incidentLog=false).
  { id: "incident-log", name: "Incident Log", desc: "H&S incidents & near misses", iconKey: "Alert", tone: "brass", group: "manage", route: "/incident-log", ssoOnly: true, visibleWhen: (c) => Boolean(c.apps?.incidentLog || c.isIncidentAdmin) },
  // BigPlanDevices (migration 105) — approve/manage the signal-box iPads for The Big Plan's STNs
  // (pairing codes, active-session, disable/delete-revoke). SSO admin tool. OR-gate (admin OR the
  // apps.bigPlanDevices grant) so admins always reach it even without the explicit app flag.
  { id: "bigplan-devices", name: "Signal Box Devices", desc: "Approve & manage signal-box iPads", iconKey: "Settings", tone: "heather", group: "manage", route: "/bigplan-devices", ssoOnly: true, visibleWhen: (c) => Boolean(c.isAdmin || c.apps?.bigPlanDevices) },

  // ── app links ───────────────────────────────────────────────────────────────
  // External NYMR apps that live alongside the Info Hub. Smaller tiles,
  // 4 per row, opened in a new tab. Slice I — "trains" moved here from
  // for-you to free up its quick-link slot for the newsletter reader.
  //
  // Phase 9 follow-up (2026-05-21) — D2: events / reports / objectives /
  // risks are gated `ssoOnly: true` because their host apps require staff
  // Entra SSO. PIN volunteers can't satisfy that login, so the tile is
  // hidden rather than dead-ending them at a login page. Live Trains stays
  // visible to everyone (public departures board, no auth).
  // featureKey liveTrackerHome — auto-hides this tile when the public tracker
  // page has been switched off from the Live Tracker app (me.features
  // .liveTrackerHome === false), so staff aren't sent to a "Not In Service"
  // page. Reappears when it's switched back on.
  { id: "trains",     name: "Live Trains", desc: "Today's departures",      iconKey: "Compass",   tone: "paper", group: "app-links", route: "https://live.nymr.co.uk",      size: "sm", featureKey: "liveTrackerHome" },
  { id: "events",     name: "Events",     desc: "Events calendar",          iconKey: "Cal",       tone: "paper", group: "app-links", route: "https://events.nymr.co.uk",     size: "sm", ssoOnly: true },
  // id "reports-site" (was "reports", which clashed with the Daily Reports tile
  // -- pins / last-used are keyed by id, so ids must be unique; 2026-11-04).
  { id: "reports-site", name: "Reports",  desc: "Operational reports",      iconKey: "Doc",       tone: "paper", group: "app-links", route: "https://reports.nymr.co.uk",    size: "sm", ssoOnly: true },
  { id: "objectives", name: "Objectives", desc: "Strategic objectives",     iconKey: "Star",      tone: "paper", group: "app-links", route: "https://objectives.nymr.co.uk", size: "sm", ssoOnly: true },
  { id: "risks",      name: "Risks",      desc: "Risk register",            iconKey: "Alert",     tone: "paper", group: "app-links", route: "https://risks.nymr.co.uk",      size: "sm", ssoOnly: true },
];

export const APPS_BY_ID: Record<string, AppEntry> = Object.fromEntries(APPS.map((a) => [a.id, a]));

export interface PermissionContext {
  isAdmin: boolean;
  isNewsAdmin?: boolean;
  isAlertsAdmin?: boolean;
  /** Phase 9 -- per-app permission grid from me.apps. When undefined, all
   *  apps render (legacy / pre-Phase-9 behaviour). */
  apps?: AppPermissions;
  /** Phase 9 follow-up (D2) -- the caller's auth method. Used to hide
   *  ssoOnly tiles from PIN sessions. When undefined the gate falls open
   *  (don't hide), preserving backwards compatibility for callers that
   *  don't pass me.authMethod. */
  authMethod?: "sso" | "pin";
  /** Sign-In System (Build 1) -- SSO-admin gate for perm: "isSsoAdmin". */
  isSsoAdmin?: boolean;
  /** Issue Manager (2026-07-23) -- me.issues.manager (admin OR granted manager
   *  rights inside the app). Gates the Issue Manager tile so it's hidden from
   *  ordinary volunteers/PIN users. */
  issuesManager?: boolean;
  /** Sign-In System (Build 1) -- HR Admin role (me.isHrAdmin). Used by the
   *  Sign In Timesheet visibleWhen predicate. */
  isHrAdmin?: boolean;
  /** Sign-In System (Build 1) -- the per-user sign_in_enabled checkbox
   *  (me.signIn.enabled). Used by the Sign In Timesheet visibleWhen predicate. */
  signInEnabled?: boolean;
  /** Sign-In System (Build 1) -- system-pref feature kill-switches from
   *  me.features. When present, a tile with a featureKey hides if its flag is
   *  false. When undefined the gate falls open. */
  featureFlags?: FeatureFlags;
  /** migration 074 -- the DV Admin role (me.isDvCheckAdmin). Gates the
   *  Driving Licence Check admin tile via its visibleWhen predicate. */
  isDvCheckAdmin?: boolean;
  /** migration 085 -- the Long Service Awards admin role
   *  (me.isLongServiceAdmin). Gates the LS Admin tile via its visibleWhen. */
  isLongServiceAdmin?: boolean;
  /** migration 094 -- the Moorsline Editor role (me.isMoorslineEditor). Gates
   *  the Moorsline editor tile via its visibleWhen. */
  isMoorslineEditor?: boolean;
  /** migration 169 -- the Vacancies Manager role (me.isVacanciesManager). With
   *  isSsoAdmin, gates the Vacancies Manager tile via its visibleWhen. */
  isVacanciesManager?: boolean;
  /** migration 124 -- the NYMR Newsletter Publisher role (me.isNewsletterPublisher).
   *  Gates the newsletter publisher tile via its visibleWhen. */
  isNewsletterPublisher?: boolean;
  /** migration 099 -- the Training Admin role (me.isTrainingAdmin). Gates the
   *  Training (Admin) tile via its visibleWhen. */
  isTrainingAdmin?: boolean;
  /** migration 101 -- Incident Log Full Access (me.isIncidentAdmin). With
   *  apps.incidentLog, gates the Incident Log tile via its visibleWhen. */
  isIncidentAdmin?: boolean;
}

export function isAppVisible(app: AppEntry, perms: PermissionContext): boolean {
  // Phase 9 follow-up D2 -- SSO-only gate. When the entry is flagged
  // ssoOnly and the caller is on a PIN session, hide the tile. Backend
  // doesn't enforce this -- the linked apps live on separate hosts -- but
  // hiding the tile is the right UX since the destination would just
  // bounce a PIN user to a login screen they can't pass.
  if (app.ssoOnly && perms.authMethod === "pin") {
    return false;
  }
  // Phase 9 -- per-app gate. When me.apps is present and the entry has an
  // appKey set, hide it unless the flag is true. Tiles without appKey
  // (calendar, vacancies-admin, app-links) are not subject to this gate
  // because they have no corresponding flag on hub_users.apps.
  if (app.appKey && perms.apps && perms.apps[app.appKey] === false) {
    return false;
  }
  // Sign-In System (Build 1) -- feature kill-switch gate. Hide when the admin
  // has turned the feature off. Falls open while me.features is still loading.
  if (app.featureKey && perms.featureFlags && perms.featureFlags[app.featureKey] === false) {
    return false;
  }
  // Sign-In System (Build 1) -- custom OR-of-conditions gate; replaces `perm`.
  if (app.visibleWhen) return app.visibleWhen(perms);
  if (!app.perm) return true;
  switch (app.perm) {
    case "isAdmin":       return perms.isAdmin;
    case "isNewsAdmin":   return Boolean(perms.isNewsAdmin);
    case "isAlertsAdmin": return Boolean(perms.isAlertsAdmin);
    case "isSsoAdmin":    return Boolean(perms.isSsoAdmin);
  }
}

/** Build the PermissionContext from /api/me. Shared by the Apps page and the
 *  Home launcher so both resolve tile visibility identically (2026-11-04). */
export function permissionContextFromMe(me: MeResponse | null): PermissionContext {
  return {
    isAdmin: Boolean(me?.isAdmin),
    isNewsAdmin: Boolean(me?.isNewsAdmin),
    isAlertsAdmin: Boolean(me?.isAlertsAdmin),
    apps: me?.apps,
    // Phase 9 follow-up D2 -- gate ssoOnly tiles for PIN volunteers. Falls
    // back to undefined while /api/me is still loading, which lets every tile
    // render briefly; the next render after `me` lands does the hide.
    authMethod: me?.authMethod,
    isSsoAdmin: Boolean(me?.isSsoAdmin),
    issuesManager: Boolean(me?.issues?.manager),
    isHrAdmin: Boolean(me?.isHrAdmin),
    signInEnabled: Boolean(me?.signIn?.enabled),
    featureFlags: me?.features,
    isDvCheckAdmin: Boolean(me?.isDvCheckAdmin),
    isLongServiceAdmin: Boolean(me?.isLongServiceAdmin),
    isMoorslineEditor: Boolean(me?.isMoorslineEditor),
    isNewsletterPublisher: Boolean(me?.isNewsletterPublisher),
    isTrainingAdmin: Boolean(me?.isTrainingAdmin),
    isIncidentAdmin: Boolean(me?.isIncidentAdmin),
    isVacanciesManager: Boolean(me?.isVacanciesManager),
  };
}

export function visibleApps(perms: PermissionContext): AppEntry[] {
  return APPS.filter((a) => isAppVisible(a, perms));
}

export function isExternalRoute(route: string): boolean {
  return /^https?:\/\//.test(route);
}
